<template>
  <div>
    <b-card>
      <div class="d-flex align-items-center justify-content-between">
        <div>
          <h3 class="mb-0">
            تفاصيل الاستبيان
          </h3>
          <b-card-text class="mb-0 text-muted">
            #{{ id }}
          </b-card-text>
        </div>

        <div class="d-flex align-items-center">
          <b-button
            v-if="survey && survey.is_created === false && hasPermission('create projects')"
            variant="primary"
            class="rounded-pill"
            @click="openCreateProjectModal"
          >
            <feather-icon
              icon="PlusIcon"
              size="18"
            />
            إنشاء مشروع
          </b-button>
        </div>
      </div>
    </b-card>

    <b-card
      v-if="loadDetails"
      class="text-center"
    >
      <b-spinner variant="primary" />
    </b-card>

    <b-card v-else-if="survey">
      <b-row>
        <b-col
          cols="12"
          md="6"
        >
          <h5 class="mb-1">
            الحالات
          </h5>
          <div class="d-flex flex-wrap">
            <b-badge
              class="mr-50 mb-50"
              :variant="statusVariant(survey.assign_status)"
            >
              Assign: {{ survey.assign_status }}
            </b-badge>
            <b-badge
              class="mr-50 mb-50"
              :variant="statusVariant(survey.survey_review_status)"
            >
              Review: {{ survey.survey_review_status }}
            </b-badge>
            <b-badge
              class="mr-50 mb-50"
              :variant="statusVariant(survey.collect_status)"
            >
              Collect: {{ survey.collect_status }}
            </b-badge>
            <b-badge
              class="mr-50 mb-50"
              :variant="survey.confirmed ? 'success' : 'secondary'"
            >
              Confirmed: {{ survey.confirmed ? 'نعم' : 'لا' }}
            </b-badge>
          </div>
        </b-col>

        <b-col
          cols="12"
          md="6"
        >
          <h5 class="mb-1">
            بيانات أساسية
          </h5>
          <ul class="list-unstyled mb-0">
            <li><strong>رقم الطلب:</strong> {{ survey.requestnumber || '-' }}</li>
            <li><strong>الاسم:</strong> {{ survey.arabicfullname || '-' }}</li>
            <li><strong>الهاتف:</strong> {{ survey.telephonenumber || '-' }}</li>
            <li><strong>المحافظة:</strong> {{ survey.gov || '-' }}</li>
            <li><strong>القطاع:</strong> {{ survey.sec || '-' }}</li>
            <li><strong>القطاع الفرعي:</strong> {{ survey.ssec || '-' }}</li>
            <li><strong>الشركة:</strong> {{ survey.companyname || '-' }}</li>
          </ul>
        </b-col>
      </b-row>

      <hr class="my-2">

      <h5 class="mb-1">
        كامل البيانات
      </h5>
      <b-table
        :items="detailsRows"
        :fields="detailsFields"
        small
        responsive
        striped
        hover
        show-empty
        empty-text="لا توجد بيانات إضافية"
      />
    </b-card>

    <!-- Create project modal -->
    <b-modal
      id="cs-create-project-modal"
      title="إنشاء مشروع من الاستبيان"
      hide-footer
    >
      <b-form @submit.prevent="submitCreateProject">
        <b-alert
          show
          variant="info"
          class="mb-2"
        >
          سيتم إرسال بيانات المشروع كـ JSON. قد يفرض السيرفر حقولًا إلزامية حسب الإعدادات.
        </b-alert>

        <b-row>
          <b-col
            cols="12"
            md="6"
          >
            <b-form-group label="المهندس المسؤول (technical_responsible_id)">
              <b-form-input
                v-model="createProjectPayload.technical_responsible_id"
                type="number"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <b-form-group label="المسؤول عن الاتفاقية (office_agreement_responsible_id)">
              <b-form-input
                v-model="createProjectPayload.office_agreement_responsible_id"
                type="number"
              />
            </b-form-group>
          </b-col>

          <b-col
            cols="12"
            md="6"
          >
            <b-form-group label="المسؤول عن الرسم (office_drawing_responsible_id)">
              <b-form-input
                v-model="createProjectPayload.office_drawing_responsible_id"
                type="number"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <b-form-group label="عميل مفوض (authorized_client_id)">
              <b-form-input
                v-model="createProjectPayload.authorized_client_id"
                type="number"
              />
            </b-form-group>
          </b-col>

          <b-col
            cols="12"
            md="6"
          >
            <b-form-group label="فريق المعاينة (inspection_teams[])">
              <b-form-input
                v-model="createProjectTeamsCsv"
                placeholder="مثال: 1,2,3"
              />
              <b-form-text>أدخل IDs مفصولة بفواصل.</b-form-text>
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <b-form-group label="التكلفة الفعلية (actual_cost)">
              <b-form-input
                v-model="createProjectPayload.actual_cost"
                type="number"
                min="0"
                step="0.01"
              />
            </b-form-group>
          </b-col>

          <b-col
            cols="12"
            md="6"
          >
            <b-form-group label="تكلفة السائق (driver_cost)">
              <b-form-input
                v-model="createProjectPayload.driver_cost"
                type="number"
                min="0"
                step="0.01"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <div class="d-flex justify-content-end">
          <b-button
            variant="outline-secondary"
            class="mr-1"
            :disabled="loadCreateProject"
            @click="closeCreateProjectModal"
          >
            إلغاء
          </b-button>
          <b-button
            type="submit"
            variant="primary"
            :disabled="loadCreateProject"
          >
            <b-spinner
              v-if="loadCreateProject"
              small
              class="mr-50"
            />
            إنشاء
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BCardText,
  BRow,
  BCol,
  BBadge,
  BSpinner,
  BTable,
  BButton,
  BModal,
  BForm,
  BFormGroup,
  BFormInput,
  BFormText,
  BAlert,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { mapGetters } from 'vuex'

export default {
  name: 'CitizenSurveyDetails',
  components: {
    BCard,
    BCardText,
    BRow,
    BCol,
    BBadge,
    BSpinner,
    BTable,
    BButton,
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BFormText,
    BAlert,
  },
  computed: {
    ...mapGetters({
      survey: 'citizenSurveys/survey',
      loadDetails: 'citizenSurveys/loadDetails',
      loadCreateProject: 'citizenSurveys/loadCreateProject',
      projectCreated: 'citizenSurveys/projectCreated',
    }),
    id() {
      return this.$route.params.id
    },
    detailsRows() {
      const s = this.survey || {}
      return Object.keys(s).map(k => ({
        key: k,
        value: typeof s[k] === 'object' ? JSON.stringify(s[k]) : String(s[k] ?? ''),
      }))
    },
  },
  data() {
    return {
      detailsFields: [
        { key: 'key', label: 'الحقل' },
        { key: 'value', label: 'القيمة' },
      ],
      createProjectPayload: {
        technical_responsible_id: null,
        office_agreement_responsible_id: null,
        office_drawing_responsible_id: null,
        authorized_client_id: null,
        actual_cost: null,
        driver_cost: null,
        inspection_teams: [],
      },
      createProjectTeamsCsv: '',
    }
  },
  watch: {
    '$route.params.id': function () {
      this.fetch()
    },
  },
  mounted() {
    this.fetch()
  },
  methods: {
    fetch() {
      this.$store.dispatch('citizenSurveys/getSurvey', { id: this.id }).catch(err => {
        const msg = err?.response?.data?.message || err?.message || 'تعذر تحميل تفاصيل الاستبيان'
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'خطأ',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: msg,
          },
        })
      })
    },
    statusVariant(val) {
      const v = String(val || '').toLowerCase()
      if (['done', 'approved', 'confirmed', 'completed', 'success', 'yes', 'true', '1'].includes(v)) return 'success'
      if (['pending', 'new', 'in_progress', 'processing', 'review'].includes(v)) return 'warning'
      if (['rejected', 'failed', 'error', 'no', 'false', '0'].includes(v)) return 'danger'
      return 'secondary'
    },
    openCreateProjectModal() {
      this.$bvModal.show('cs-create-project-modal')
    },
    closeCreateProjectModal() {
      this.$bvModal.hide('cs-create-project-modal')
    },
    submitCreateProject() {
      // parse teams
      const raw = (this.createProjectTeamsCsv || '').trim()
      const teams = raw
        ? raw
          .split(',')
          .map(x => String(x).trim())
          .filter(Boolean)
          .map(x => Number(x))
          .filter(n => Number.isFinite(n))
        : []

      const payload = {
        ...this.createProjectPayload,
        inspection_teams: teams,
      }

      this.$store
        .dispatch('citizenSurveys/createProjectFromSurvey', { id: this.id, data: payload })
        .then(res => {
          const created = res?.data || null
          this.$toast({
            component: ToastificationContent,
            props: {
              title: res?.message || 'تم إنشاء المشروع',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })

          // try navigate to project details if route exists and project id available
          const projectId = created?.id || created?.data?.id
          if (projectId) {
            this.closeCreateProjectModal()
            this.$router.push({ name: 'project_details', params: { id: projectId } })
          }
        })
        .catch(err => {
          const msg = err?.response?.data?.message || err?.message || 'حدث خطأ أثناء إنشاء المشروع'
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'خطأ',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: msg,
            },
          })
        })
    },
  },
}
</script>
